import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import US_STATES_DATA from "./states";
import {
  MantineProvider,
  Button,
  TextInput,
  NumberInput,
  Checkbox,
  Radio,
  Loader,
  SegmentedControl,
  Select,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Footer } from "../../components/Footer/Footer";
import { usePricingDetailsList } from "../../hooks/usePricingDetails";
import { djangoAxiosInstance } from "../../api";
import {
  Plan,
  PlanDescription,
  Step,
  PricingDetailsResponse,
  ProcessedPlans,
  FormValues,
  PricingInfo,
} from "./types";
import { endpoints } from "../../api/constants/endpoints";

const theme = {
  colors: {
    primary: [
      "#f0f5f5",
      "#d9e6e6",
      "#b3cdcd",
      "#8db3b3",
      "#669a9a",
      "#408080",
      "#336666",
      "#264d4d",
      "#193333",
      "#0f2d2d",
    ],
    accent: [
      "#f4efe5",
      "#e9dfcc",
      "#d3c0a0",
      "#c2aa7d",
      "#aa9664",
      "#8d7951",
      "#70613f",
      "#53482f",
      "#37301f",
      "#1a1710",
    ],
  },
  primaryColor: "primary",
  components: {
    Button: {
      styles: {
        root: {
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            backgroundColor: "var(--accent-gold-hover)",
            color: "white",
          },
        },
      },
    },
  },
};

const PLAN_DESCRIPTIONS: Record<string, PlanDescription> = {
  basic: {
    title: "Basic",
    description:
      "For lawyers getting acquainted with AI-powered legal work—streamline drafting and automate routine tasks with ease.",
  },
  pro: {
    title: "Professional",
    description:
      "For lawyers handling high-volume work who want the power of AI with seamless integrations and partner tools.",
  },
};

const CheckoutPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState<string>("selectPlan");
  const steps: Step[] = [
    { key: "selectPlan", title: "Select Plan", number: 1 },
    { key: "account", title: "Your Account", number: 2 },
    { key: "seats", title: "Number of Seats", number: 3 },
    { key: "payment", title: "Payment", number: 4 },
  ];

  const pricingData: PricingDetailsResponse = usePricingDetailsList();

  const { plans, isLoading }: ProcessedPlans = useMemo(() => {
    if (!pricingData || !pricingData.pricing) {
      return { plans: [], isLoading: true };
    }

    const { pricing } = pricingData;

    const processedPlans: Plan[] = [
      {
        id: "basic",
        title: PLAN_DESCRIPTIONS.basic.title,
        description: PLAN_DESCRIPTIONS.basic.description,
        monthlyPrice: pricing.basic?.monthly_price
          ? pricing.basic.monthly_price / 100
          : null,
        monthlyUUID: pricing.basic?.monthly_id,
        annualPrice: pricing.basic?.annual_price
          ? pricing.basic.annual_price / 100
          : null,
        annualUUID: pricing.basic?.annual_id,
      },
      {
        id: "pro",
        title: PLAN_DESCRIPTIONS.pro.title,
        description: PLAN_DESCRIPTIONS.pro.description,
        monthlyPrice: pricing.pro?.monthly_price
          ? pricing.pro.monthly_price / 100
          : null,
        monthlyUUID: pricing.pro?.monthly_id,
        annualPrice: pricing.pro?.annual_price
          ? pricing.pro.annual_price / 100
          : null,
        annualUUID: pricing.pro?.annual_id,
      },
    ];

    return { plans: processedPlans, isLoading: false };
  }, [pricingData]);
  const initialPlan = searchParams.get("plan") || "basic";
  const initialBillingCycle = searchParams.get("billing") || "monthly";
  const form = useForm<FormValues>({
    initialValues: {
      selectedPlan: initialPlan,
      billingCycle: initialBillingCycle,
      numSeats: 1,

      firstName: "",
      lastName: "",

      companyName: "",
      taxId: "",

      streetAddress: "",
      streetAddress2: "",
      city: "",
      zipCode: "",

      agreeToTerms: false,
      agreeToMarketing: false,
      isAttorney: false,
      state: "",
    },
    validate: {
      firstName: (value) =>
        value.trim().length < 2
          ? "First name must be at least 2 characters"
          : null,
      lastName: (value) =>
        value.trim().length < 2
          ? "Last name must be at least 2 characters"
          : null,
      streetAddress: (value) =>
        value.trim().length < 5 ? "Please enter a valid street address" : null,
      city: (value) =>
        value.trim().length < 2 ? "Please enter a valid city" : null,
      zipCode: (value) =>
        /^\d{5}(-\d{4})?$/.test(value) ? null : "Please enter a valid ZIP code",
      state: (value) => (!value ? "Please select a state" : null),
      numSeats: (value) => (value < 1 ? "Must have at least one seat" : null),
      taxId: (value) => {
        if (value.trim() === "") return null;

        return /^\d{2}-\d{7}$/.test(value)
          ? null
          : "Please enter a valid EIN (XX-XXXXXXX format)";
      },
      agreeToTerms: (value) =>
        value === false ? "You must agree to the terms" : null,
      isAttorney: (value) =>
        value === false
          ? "You must confirm you are a licensed attorney or work under one"
          : null,
    },
  });

  const pricingInfo: PricingInfo = useMemo(() => {
    const selectedPlan =
      plans.find((plan) => plan.id === form.values.selectedPlan) ||
      ({} as Plan);
    const isMonthly = form.values.billingCycle === "monthly";

    const pricePerSeat = isMonthly
      ? selectedPlan.monthlyPrice || 0
      : selectedPlan.annualPrice || 0;

    const annualSavings =
      selectedPlan.monthlyPrice && selectedPlan.annualPrice
        ? selectedPlan.monthlyPrice * 12 - selectedPlan.annualPrice
        : 0;

    const billingPeriod = isMonthly ? "month" : "year";
    const total = form.values.numSeats * pricePerSeat;

    return {
      selectedPlan,
      isMonthly,
      pricePerSeat,
      billingPeriod,
      total,
    };
  }, [
    plans,
    form.values.selectedPlan,
    form.values.billingCycle,
    form.values.numSeats,
  ]);

  const { selectedPlan, isMonthly, pricePerSeat, billingPeriod, total } =
    pricingInfo;

  const handleContinue = (nextStep: string): void => {
    let canContinue = true;

    // A workaround to validate only fields from form, without those on the right with agreements
    if (activeStep === "account") {
      const fieldsToValidate = [
        "firstName",
        "lastName",
        "companyName",
        "taxId",
        "streetAddress",
        "streetAddress2",
        "city",
        "zipCode",
        "state",
        "numSeats",
      ];

      const errors: Record<string, boolean> = {};
      fieldsToValidate.forEach((field) => {
        const fieldError = form.validateField(field);
        if (fieldError.hasError) {
          errors[field] = true;
        }
      });

      canContinue = Object.keys(errors).length === 0;
    }

    if (canContinue) {
      setActiveStep(nextStep);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const validation = form.validate();

    if (validation.hasErrors) {
      const firstErrorField = Object.keys(validation.errors)[0];
      document.getElementsByName(firstErrorField)[0]?.focus();
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const selectedPlanObj = plans.find(
      (plan) => plan.id === form.values.selectedPlan,
    );

    const planUUID =
      form.values.billingCycle === "annual"
        ? selectedPlanObj?.annualUUID
        : selectedPlanObj?.monthlyUUID;

    try {
      const orderData = {
        subscription_plan: planUUID,
        is_annual: form.values.billingCycle === "annual",
        seat_limit: form.values.numSeats,
        agree_to_marketing: form.values.agreeToMarketing,

        billing: {
          company_name: form.values.companyName,
          tax_id: form.values.taxId,
          first_name: form.values.firstName,
          last_name: form.values.lastName,
          street_address: form.values.streetAddress,
          street_address2: form.values.streetAddress2,
          city: form.values.city,
          zip_code: form.values.zipCode,
          state: form.values.state,
        },
      };

      const accessToken = localStorage.getItem("access_token");
      const headers = {};
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await djangoAxiosInstance.post(
        endpoints.CREATE_CHECKOUT_SESSION,
        orderData,
        { headers },
      );
      const data = response.data;

      if (data.url) {
        window.location.href = data.url;
      } else {
        setSuccess(true);
      }
      console.log("Checkout session created:", data);
    } catch (error) {
      console.error("Payment error:", error);
      setError(
        error.message || "An error occurred while processing your payment",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const StepHeader = ({ title, stepKey, stepNumber }) => {
    const isActive = activeStep === stepKey;
    const isCompleted =
      steps.findIndex((step) => step.key === stepKey) <
      steps.findIndex((step) => step.key === activeStep);

    return (
      <div
        className="flex justify-between items-center mb-4"
        aria-current={isActive ? "step" : undefined}
      >
        <div className="flex items-center space-x-3">
          <div
            className={`w-8 h-8 flex items-center justify-center rounded-full ${
              isActive
                ? "bg-[var(--primary-color)] text-white"
                : isCompleted
                  ? "bg-[var(--accent-gold)] text-white"
                  : "border border-gray-300 text-gray-500"
            }`}
            aria-hidden="true"
          >
            {isCompleted ? "✓" : stepNumber}
          </div>
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
        </div>
        {!isActive && isCompleted && (
          <button
            onClick={() => setActiveStep(stepKey)}
            aria-label={`Edit ${title}`}
            className="text-[var(--accent-gold)] hover:text-[var(--accent-gold-hover)]"
          >
            ✏️ Edit
          </button>
        )}
      </div>
    );
  };

  const currentStepIndex = steps.findIndex((step) => step.key === activeStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  return (
    <MantineProvider>
      <main className="min-h-screen bg-gradient-to-r from-gray-100 to-gray-200 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress bar */}
          <div className="mb-8 px-4">
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-500 ease-in-out"
                style={{ width: `${progress}%` }}
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>Start</span>
              <span>Complete Purchase</span>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">
              <p className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </p>
              <button
                onClick={() => setError(null)}
                className="absolute top-3 right-3 text-red-700"
                aria-label="Dismiss error"
              >
                ×
              </button>
            </div>
          )}

          {success ? (
            <div className="bg-white shadow-lg rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-green-500 text-3xl">✓</span>
              </div>
              <h2 className="text-2xl font-bold mb-2">
                Thank You For Your Purchase!
              </h2>
              <p className="text-gray-600 mb-6">
                Your order has been processed successfully.
              </p>
              <p className="text-gray-800 mb-4">
                We've sent a confirmation email to your inbox with details about
                your purchase.
              </p>
              <Button
                component="a"
                href="/dashboard"
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Go to Dashboard
              </Button>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (activeStep === "payment") {
                  handleSubmit();
                } else {
                  // Find next step
                  const currentIndex = steps.findIndex(
                    (step) => step.key === activeStep,
                  );
                  const nextStep = steps[currentIndex + 1]?.key || "payment";
                  handleContinue(nextStep);
                }
              }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-8">
                  <div className="bg-white shadow-lg rounded-lg p-8">
                    <StepHeader
                      title="Select Plan"
                      stepKey="selectPlan"
                      stepNumber="1"
                    />
                    {activeStep === "selectPlan" && (
                      <>
                        <div className="mb-8">
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Billing Cycle
                          </label>
                          <SegmentedControl
                            value={form.values.billingCycle}
                            onChange={(value) => {
                              form.setFieldValue("billingCycle", value);
                              setSearchParams({ plan: form.values.selectedPlan, billing: value });
                            }}
                            data={[
                              { label: "Monthly", value: "monthly" },
                              { label: "Annual", value: "annual" },
                            ]}
                            classNames={{
                              root: "bg-gray-100 p-1 rounded-lg",
                              active: "bg-white shadow-sm rounded-md",
                              control: "p-2",
                              label: "font-medium",
                            }}
                          />
                        </div>

                        {!isLoading ? (
                          <div className="flex flex-col md:flex-row gap-4">
                            {plans.map((plan) => {
                              const price =
                                form.values.billingCycle === "monthly"
                                  ? plan.monthlyPrice
                                  : plan.annualPrice ||
                                    (plan.monthlyPrice
                                      ? plan.monthlyPrice * 12 * 0.9
                                      : null);

                              if (price === null) return null;

                              return (
                                <div
                                  key={plan.id}
                                  onClick={() => {
                                    form.setFieldValue("selectedPlan", plan.id);
                                    setSearchParams({ plan: plan.id, billing: form.values.billingCycle });
                                  }}
                                  className={`flex-1 border cursor-pointer rounded-lg p-6 transition duration-200 ${
                                    form.values.selectedPlan === plan.id
                                      ? "border-[var(--accent-gold)] bg-[#f9f7f2]"
                                      : "border-gray-300 hover:border-[var(--accent-gold)]"
                                  }`}
                                >
                                  <div className="flex items-center space-x-3">
                                    <Radio
                                      value={plan.id}
                                      name="selectedPlan"
                                      checked={
                                        form.values.selectedPlan === plan.id
                                      }
                                      onChange={() => {}}
                                      className="cursor-pointer"
                                    />
                                    <h3 className="text-lg font-semibold text-gray-800">
                                      {plan.title}
                                    </h3>
                                  </div>
                                  <p className="mt-4 text-sm text-gray-600">
                                    {plan.description}
                                  </p>
                                  <p className="mt-2 text-lg font-bold text-[var(--primary-color)]">
                                    ${price.toFixed(2)} per seat
                                    {form.values.billingCycle === "monthly"
                                      ? " monthly"
                                      : " yearly"}
                                  </p>
                                  {form.values.billingCycle === "annual" &&
                                    plan.monthlyPrice &&
                                    plan.annualPrice &&
                                    plan.monthlyPrice * 12 - plan.annualPrice >
                                      0 && (
                                      <p className="text-sm text-[var(--secondary-color)] mt-1">
                                        Save $
                                        {(
                                          plan.monthlyPrice * 12 -
                                          plan.annualPrice
                                        ).toFixed(2)}{" "}
                                        per seat annually
                                      </p>
                                    )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center p-10">
                            <Loader size="md" />
                            <span className="ml-3">
                              Loading pricing information...
                            </span>
                          </div>
                        )}

                        <div className="flex justify-end mt-6">
                          <Button
                            type="submit"
                            disabled={isLoading}
                            className="px-6 py-3 bg-[var(--accent-gold)] text-white rounded-lg hover:bg-[var(--accent-gold-hover)] transition"
                          >
                            Continue
                          </Button>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="bg-white shadow-lg rounded-lg p-8">
                    <StepHeader
                      title="Your Account & Billing Information"
                      stepKey="account"
                      stepNumber="2"
                    />
                    {activeStep === "account" && (
                      <>
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                              Personal Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <TextInput
                                label="First Name"
                                placeholder="John"
                                name="firstName"
                                required
                                value={form.values.firstName}
                                onChange={(e) =>
                                  form.setFieldValue(
                                    "firstName",
                                    e.target.value,
                                  )
                                }
                                error={form.errors.firstName}
                                classNames={{
                                  input:
                                    "mt-2 w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                }}
                              />
                              <TextInput
                                label="Last Name"
                                placeholder="Doe"
                                name="lastName"
                                required
                                value={form.values.lastName}
                                onChange={(e) =>
                                  form.setFieldValue("lastName", e.target.value)
                                }
                                error={form.errors.lastName}
                                classNames={{
                                  input:
                                    "mt-2 w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                }}
                              />
                            </div>
                          </div>

                          <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                              Company Information (Optional)
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <TextInput
                                label="Company Name"
                                placeholder="Legal Firm LLC"
                                name="companyName"
                                value={form.values.companyName}
                                onChange={(e) =>
                                  form.setFieldValue(
                                    "companyName",
                                    e.target.value,
                                  )
                                }
                                classNames={{
                                  input:
                                    "mt-2 w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                }}
                              />
                              <TextInput
                                label="Tax ID / EIN"
                                placeholder="XX-XXXXXXX"
                                name="taxId"
                                value={form.values.taxId}
                                onChange={(e) =>
                                  form.setFieldValue("taxId", e.target.value)
                                }
                                error={form.errors.taxId}
                                classNames={{
                                  input:
                                    "mt-2 w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                }}
                              />
                            </div>
                          </div>

                          <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                              Billing Address
                            </h3>
                            <div className="space-y-6">
                              <TextInput
                                label="Street Address"
                                placeholder="123 Main Street"
                                name="streetAddress"
                                required
                                value={form.values.streetAddress}
                                onChange={(e) =>
                                  form.setFieldValue(
                                    "streetAddress",
                                    e.target.value,
                                  )
                                }
                                error={form.errors.streetAddress}
                                classNames={{
                                  input:
                                    "mt-2 w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                }}
                              />
                              <TextInput
                                label="Apartment, suite, etc. (optional)"
                                placeholder="Suite 100"
                                name="streetAddress2"
                                value={form.values.streetAddress2}
                                onChange={(e) =>
                                  form.setFieldValue(
                                    "streetAddress2",
                                    e.target.value,
                                  )
                                }
                                classNames={{
                                  input:
                                    "mt-2 w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                }}
                              />

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <TextInput
                                  label="City"
                                  placeholder="San Francisco"
                                  name="city"
                                  required
                                  value={form.values.city}
                                  onChange={(e) =>
                                    form.setFieldValue("city", e.target.value)
                                  }
                                  error={form.errors.city}
                                  classNames={{
                                    input:
                                      "mt-2 w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                  }}
                                />
                                <div>
                                  <Select
                                    label="State"
                                    placeholder="Select a state"
                                    required
                                    searchable
                                    data={US_STATES_DATA}
                                    value={form.values.state}
                                    onChange={(value) =>
                                      form.setFieldValue("state", value)
                                    }
                                    nothingFoundMessage="Please type in correct state..."
                                    checkIconPosition="left"
                                    error={form.errors.state}
                                    classNames={{
                                      input:
                                        "mt-2 w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                    }}
                                  />
                                  {form.errors.state && (
                                    <div className="text-red-500 text-xs mt-1">
                                      {form.errors.state}
                                    </div>
                                  )}
                                </div>
                                <TextInput
                                  label="ZIP Code"
                                  placeholder="94105"
                                  name="zipCode"
                                  required
                                  value={form.values.zipCode}
                                  onChange={(e) =>
                                    form.setFieldValue(
                                      "zipCode",
                                      e.target.value,
                                    )
                                  }
                                  error={form.errors.zipCode}
                                  classNames={{
                                    input:
                                      "mt-2 w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-between mt-8">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setActiveStep("selectPlan")}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                          >
                            Back
                          </Button>
                          <Button
                            type="submit"
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                          >
                            Continue
                          </Button>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="bg-white shadow-lg rounded-lg p-8">
                    <StepHeader
                      title="Number of Seats"
                      stepKey="seats"
                      stepNumber="3"
                    />
                    {activeStep === "seats" && (
                      <>
                        <div
                          data-testid="numberOfSeatsFields"
                          className="flex flex-col md:flex-row items-center gap-4 md:gap-8"
                        >
                          <div className="flex flex-col w-full md:w-auto">
                            <label className="text-sm font-medium text-gray-700 mb-1">
                              Number of seats
                            </label>
                            <NumberInput
                              name="numSeats"
                              data-testid="seats"
                              min={1}
                              max={100}
                              value={form.values.numSeats}
                              onChange={(value) =>
                                form.setFieldValue("numSeats", value)
                              }
                              error={form.errors.numSeats}
                              className="w-full md:w-24"
                              classNames={{
                                input:
                                  "border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500",
                              }}
                            />
                          </div>
                          <div className="hidden md:block">
                            <span className="text-lg font-semibold">×</span>
                          </div>
                          <div className="flex flex-col w-full md:w-auto">
                            <label className="text-sm font-medium text-gray-700 mb-1">
                              Cost per {billingPeriod}/seat
                            </label>
                            <TextInput
                              value={`${pricePerSeat.toFixed(2)}`}
                              disabled
                              className="w-full md:w-32"
                              classNames={{
                                input:
                                  "border border-gray-300 rounded px-3 py-2 bg-gray-50",
                              }}
                            />
                          </div>
                          <div className="hidden md:block">
                            <span className="text-lg font-semibold">=</span>
                          </div>
                          <div className="flex flex-col w-full md:w-auto">
                            <label className="text-sm font-medium text-gray-700 mb-1">
                              {form.values.billingCycle === "monthly"
                                ? "Monthly"
                                : "Annual"}{" "}
                              total
                            </label>
                            <TextInput
                              value={`${total.toFixed(2)}`}
                              disabled
                              className="w-full md:w-32"
                              classNames={{
                                input:
                                  "border border-gray-300 rounded px-3 py-2 bg-gray-50 font-bold text-blue-600",
                              }}
                            />
                          </div>
                        </div>

                        <div className="flex justify-between mt-10">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setActiveStep("account")}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                          >
                            Back
                          </Button>
                          <Button
                            type="submit"
                            className="px-6 py-3 bg-[var(--accent-gold)] text-white rounded-lg hover:bg-[var(--accent-gold-hover)] transition"
                          >
                            Continue to Payment
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="lg:col-span-1">
                  <div className="bg-white shadow-lg rounded-lg p-8 sticky top-6">
                    <h2 className="text-xl font-semibold text-gray-600 mb-4">
                      ORDER SUMMARY
                    </h2>
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">
                      Socius Legal{" "}
                      {form.values.selectedPlan === "basic"
                        ? "Basic"
                        : "Professional"}
                    </h3>

                    <div className="flex justify-between items-center mb-1">
                      <p className="text-sm font-medium text-gray-700">
                        {form.values.numSeats} seat
                        {form.values.numSeats > 1 ? "s" : ""} for 1{" "}
                        {billingPeriod} of{" "}
                        {form.values.selectedPlan === "basic"
                          ? "Basic"
                          : "Professional"}{" "}
                        plan
                      </p>
                      <p className="text-sm font-medium text-gray-700">
                        {pricePerSeat.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </p>
                    </div>

                    <div className="mb-4">
                      <p className="text-xs text-gray-500">
                        {form.values.billingCycle === "monthly"
                          ? "Billed monthly"
                          : "Billed annually"}
                      </p>
                    </div>

                    <hr className="border-t my-4" />
                    <div className="flex justify-between items-center mb-6">
                      <p className="text-lg font-bold text-gray-900">
                        {form.values.billingCycle === "monthly"
                          ? "MONTHLY TOTAL"
                          : "ANNUAL TOTAL"}
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {total.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </p>
                    </div>

                    <div className="space-y-4 mb-6">
                      <Checkbox
                        id="agreeToTerms"
                        label={
                          <span className="text-sm text-gray-700">
                            I agree to the{" "}
                            <a
                              target="_blank"
                              href="/terms-of-service/"
                              className="underline text-blue-600 hover:text-blue-800"
                            >
                              Terms of Service
                            </a>
                            .
                          </span>
                        }
                        checked={form.values.agreeToTerms}
                        onChange={(event) =>
                          form.setFieldValue(
                            "agreeToTerms",
                            event.currentTarget.checked,
                          )
                        }
                        error={form.errors.agreeToTerms}
                      />
                      <Checkbox
                        id="isAttorney"
                        label={
                          <span className="text-sm text-gray-700">
                            I confirm that I am a licensed attorney or work
                            under the direct supervision of a licensed attorney.
                          </span>
                        }
                        checked={form.values.isAttorney}
                        onChange={(event) =>
                          form.setFieldValue(
                            "isAttorney",
                            event.currentTarget.checked,
                          )
                        }
                        error={form.errors.isAttorney}
                      />
                      <Checkbox
                        id="agreeToMarketing"
                        label={
                          <span className="text-sm text-gray-700">
                            I agree to receive marketing emails from Socius
                            Legal
                          </span>
                        }
                        checked={form.values.agreeToMarketing}
                        onChange={(event) =>
                          form.setFieldValue(
                            "agreeToMarketing",
                            event.currentTarget.checked,
                          )
                        }
                      />
                    </div>

                    {activeStep === "payment" && (
                      <>
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full px-6 py-3 bg-[var(--accent-gold)] text-white rounded-lg hover:bg-[var(--accent-gold-hover)] transition mb-2 flex items-center justify-center"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader
                                size="sm"
                                color="white"
                                className="mr-2"
                              />
                              PROCESSING...
                            </>
                          ) : (
                            "COMPLETE MY PURCHASE"
                          )}
                        </Button>
                        <p className="text-xs text-gray-500 text-center">
                          Renews automatically. Cancel anytime.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </MantineProvider>
  );
};

export default CheckoutPage;
